/* Shared building blocks: client-side links, screenshot slots, CTA buttons. */

import React from 'react';
import { ACCENT, DEMO_BOOKING_URL } from './data.js';

export const NavContext = React.createContext({ navigate: () => {} });

// A real <a href> so crawlers and middle-click work; plain left-clicks are
// routed client-side.
export function Link({ to, onClick, ...rest }) {
  const { navigate } = React.useContext(NavContext);
  const handle = (e) => {
    if (onClick) onClick(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return <a href={to} onClick={handle} {...rest} />;
}

// Smooth-scroll to an element on the current page, offset by the fixed nav.
export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 68, behavior: reduce ? 'auto' : 'smooth' });
}

export function openDemoBooking() {
  window.open(DEMO_BOOKING_URL, '_blank', 'noopener,noreferrer');
}

export function Icon({ name }) {
  return <span className="icon" aria-hidden="true">{name}</span>;
}

// Primary CTA. Every "book a demo" style button scrolls to the contact band.
export function ContactButton({ children, size = '', icon }) {
  return (
    <a href="#contact" className={'btn btn-primary ' + size}
       onClick={(e) => { e.preventDefault(); scrollToId('contact'); }}>
      {icon && <Icon name={icon} />}<span>{children}</span>
    </a>
  );
}

// A screenshot, or a captioned placeholder when there is no image yet.
export function Shot({ src, alt }) {
  if (src) return <img className="shot-img" src={src} alt={alt} loading="lazy" decoding="async" />;
  return <div className="shot-ph"><span>{alt}</span></div>;
}

// Product accent as CSS custom properties for a subtree.
export function accentVars(key) {
  const ac = ACCENT[key];
  return { '--pac': ac, '--psoft': hexA(ac, .14), '--pglow': hexA(ac, .3) };
}

export function hexA(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
}

// Light accents carry dark text; the navy and violet carry white.
const LIGHT = ['#22D3B0', '#2ED3A0', '#FFB547', '#A9C1FF'];
export const onAccent = (c) => (LIGHT.includes(c) ? '#0B1430' : '#FFFFFF');

// Three-colour rotation used for How / Features / Use-case cards.
export function palette(key) {
  const ac = ACCENT[key];
  return ['#1E3A8A', ac, ac === '#8B7CFF' ? '#2ED3A0' : '#8B7CFF'];
}
